import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '../components/common/EmptyState';
import { ProjectGuard } from '../components/common/ProjectGuard';
import { GenerateOutlineDialog } from '../components/outline/GenerateOutlineDialog';
import { useProjectStore } from '../stores/project.store';
import { useProject, type OutlineSummaryDto } from '../api/queries/projects';
import { formatDistanceToNow } from '../lib/dates';

export function OutlineListRoute() {
  const { activeProjectId } = useProjectStore();

  return (
    <ProjectGuard>
      {activeProjectId && <OutlineListContent projectId={activeProjectId} />}
    </ProjectGuard>
  );
}

function OutlineListContent({ projectId }: { projectId: string }) {
  const { data: project, isLoading, isError } = useProject(projectId);
  const navigate = useNavigate();
  const [generateOpen, setGenerateOpen] = useState(false);

  const outlines = project?.outlines ?? [];

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Outlines{project ? ` \u2014 ${project.name}` : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={<SmartToyIcon />}
          onClick={() => setGenerateOpen(true)}
        >
          Generate New
        </Button>
      </Stack>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Failed to load project.</Alert>}

      <Grid container spacing={2}>
        {outlines.map((item: OutlineSummaryDto) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
            <Card
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                navigate({
                  to: '/outlines/$outlineId',
                  params: { outlineId: item.id },
                })
              }
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {item.isPrimary && (
                    <Chip
                      size="small"
                      label={'\u2605 Primary'}
                      color="primary"
                    />
                  )}
                  <Typography variant="h6" noWrap>
                    v{item.versionNumber}{' '}
                    {item.label ? `\u2014 ${item.label}` : ''}
                  </Typography>
                </Stack>
                {item.sourceModel && (
                  <Typography variant="body2" color="text.secondary">
                    AI ({item.sourceModel})
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Chip
                    size="small"
                    label={`${item.nodeCount} node${item.nodeCount === 1 ? '' : 's'}`}
                  />
                  {item.stage && (
                    <Chip size="small" label={item.stage} variant="outlined" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(item.updatedAt)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!isLoading && outlines.length === 0 && (
        <EmptyState
          icon={<AccountTreeIcon sx={{ fontSize: 64 }} />}
          title="No outlines yet"
          description="Generate an AI outline to plan your story structure. Organize chapters, scenes, and plot points before you write."
          ctaLabel="Generate Outline"
          onCtaClick={() => setGenerateOpen(true)}
        />
      )}

      <GenerateOutlineDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        projectId={projectId}
      />
    </Paper>
  );
}
